import React, { useContext, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import ProgressCreateFields from 'components/progress/ProgressCreateFields';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultProgressFormValidations from 'common/validations/form/defaultProgress';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import getDataToSave from 'common/form/dataToSave';
import { createProgressInitialValues } from 'common/form/initial-values/create';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import { handleClientIdChange } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import progressSlice from 'store/modules/progress';

const requiredFields = [
  'back_picture',
  'created_at',
  'end_date',
  'front_picture',
  'height',
  'profile_picture',
  'start_date',
  'user_id',
  'weight'
];

function ProgressCreate() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('progress.create', { ns: 'routes' });
  const formRef = useRef(null);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const clientOptions = useSelector((state) => state.client.data.search);
  const searchStatus = useSelector((state) => state.client.status.search);
  const status = useSelector((state) => state.progress.status);

  useEffect(() => {
    if (status === requestStatus.success) {
      formRef.current.restart();
    }
  }, [status]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onClientIdChange = handleClientIdChange(dispatch);

  const onCreate = (values) => {
    const dataToSave = getDataToSave(values, 'progress');
    dispatch(progressSlice.actions.create(dataToSave));
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={createProgressInitialValues}
        onSubmit={onCreate}
        validate={defaultProgressFormValidations}
        render={({ handleSubmit, form, invalid, pristine }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <ProgressCreateFields
                clientOptions={clientOptions}
                onClientIdChange={onClientIdChange}
                requiredFields={requiredFields}
                searchStatus={searchStatus}
              />
              <StyledButtonsContainer>
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listProgress.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('progress.create', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
            </form>
          );
        }}
      />
    </>
  );
}

export default ProgressCreate;
